import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FileSearch, Loader2, MailWarning, Search } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, apiError, formatDate, RECORD_STATUSES } from "@/lib/api";
import { ADMIN_NAV } from "./nav";

export default function AdminRecords() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [records, setRecords] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(
    params.get("delivery") === "pending" ? "issued-not-sent" : "all"
  );

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/records", {
      params: {
        search,
        status: ["all", "issued-not-sent"].includes(status) ? "" : status,
        delivery: status === "issued-not-sent" ? "pending" : "",
      },
    });
    setRecords(data);
  }, [search, status]);

  useEffect(() => {
    const t = setTimeout(() => load().catch((e) => toast.error(apiError(e))), 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <AppShell
      nav={ADMIN_NAV}
      title="Academic Records"
      subtitle="Partner-submitted record packages awaiting ACS review"
    >
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            className="pl-9"
            placeholder="Search by student name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="record-search-input"
          />
        </div>
        <Select
          value={status}
          onValueChange={(v) => {
            setStatus(v);
            if (v === "issued-not-sent") setParams({ delivery: "pending" });
            else setParams({});
          }}
        >
          <SelectTrigger className="w-56" data-testid="record-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="issued-not-sent">Issued – Not Sent</SelectItem>
            {RECORD_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-200">
        <CardContent className="overflow-x-auto p-0">
          {!records ? (
            <div className="p-6">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : (
            <Table data-testid="records-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead>Academic year</TableHead>
                  <TableHead>Partner</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Versions</TableHead>
                  <TableHead>Flagged</TableHead>
                  <TableHead>Documents</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Review</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-sm text-slate-500">
                      No records yet. Records appear here once a partner uploads a package.
                    </TableCell>
                  </TableRow>
                )}
                {records.map((r) => (
                  <TableRow key={r.id} data-testid={`record-row-${r.id}`}>
                    <TableCell className="font-medium text-slate-900">{r.student_name}</TableCell>
                    <TableCell className="text-sm">{r.grade_level || "—"}</TableCell>
                    <TableCell className="text-sm">{r.academic_year || "—"}</TableCell>
                    <TableCell className="text-sm">{r.partner_name || "—"}</TableCell>
                    <TableCell>
                      <StatusBadge status={r.status} testId={`record-status-${r.id}`} />
                      {r.awaiting_delivery && (
                        <span
                          className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-amber-700"
                          data-testid={`issued-not-sent-${r.id}`}
                        >
                          <MailWarning className="h-3 w-3" /> Issued – Not Sent
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{r.versions}</TableCell>
                    <TableCell>
                      {r.flagged_count > 0 ? (
                        <span className="text-xs font-semibold text-amber-700">
                          {r.flagged_count} flagged
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell>{r.documents}</TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {formatDate(r.updated_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        onClick={() => navigate(`/admin/records/${r.id}`)}
                        data-testid={`open-record-${r.id}`}
                      >
                        <FileSearch className="h-4 w-4" /> Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
